/** Multi-line input. Same label/error contract as `Input`, for the same reason. */

import { forwardRef, useId, type TextareaHTMLAttributes } from "react";

import { cx } from "./cx";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, hint, className, id, rows = 4, ...rest },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={fieldId} className="text-label-md text-textPrimary">
        {label}
      </label>
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={cx(hintId, errorId) || undefined}
        className={cx(
          "rounded-sm border bg-surfaceRaised px-3 py-2 text-body-md text-textPrimary",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
          // Vertical only: horizontal resize breaks the grid and is never what anyone wanted.
          "resize-y",
          error ? "border-statusDanger" : "border-borderStrong",
          className,
        )}
        {...rest}
      />
      {hint ? (
        <span id={hintId} className="text-label-md text-textMuted">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} role="alert" className="text-label-md text-statusDanger">
          {error}
        </span>
      ) : null}
    </div>
  );
});
